import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Plus, Trash2, ArrowRight } from "lucide-react";
import { Card, PageHeader, Btn, inputCls, Empty } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

export default function Brand() {
  const [profile, setProfile] = useState(null);
  const [pillars, setPillars] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [p, pl] = await Promise.all([api.get("/brand-profile"), api.get("/engine/pillars")]);
    setProfile(p.data);
    setPillars(pl.data);
  };

  useEffect(() => {
    load().catch(() => setProfile(false));
  }, []);

  const quick = profile?.steps?.quick || {};

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/engine/pillars", { pillars });
      setPillars(data);
      toast.success("Your content pillars are saved.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const suggest = async () => {
    setBusy("suggest");
    try {
      const { data } = await api.post("/engine/pillars/suggest");
      const suggested = (data?.pillars || []).map((p, i) => ({
        name: p.name, why: p.why || "", enabled: true, priority: pillars.length + i + 1,
      }));
      const existing = pillars.map((p) => p.name.toLowerCase());
      setPillars([...pillars, ...suggested.filter((s) => !existing.includes(s.name.toLowerCase()))]);
      toast.success("Suggestions added — keep the ones you like and save.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const update = (i, patch) => setPillars((list) => list.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  if (profile === null) return <Card testId="brand-loading">Loading your brand…</Card>;

  return (
    <div data-testid="brand-page">
      <PageHeader
        testId="brand-header"
        title="Your brand"
        body="This is what PostMint knows about your business. Everything here shapes the content it writes."
      />

      <Card testId="brand-summary-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <p className="font-display text-lg font-semibold text-slate-900">Brand Profile</p>
          <Link
            to="/dashboard/brand-profile"
            data-testid="brand-advanced-link"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
          >
            Advanced Brand Profile <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        {Object.keys(quick).length === 0 ? (
          <div className="mt-4">
            <p className="text-sm text-slate-600">
              You haven't run the quick setup yet. It takes a couple of minutes and PostMint fills in most of it.
            </p>
            <Link
              to="/dashboard/setup"
              data-testid="brand-run-setup-link"
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
            >
              Run quick setup <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        ) : (
          <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="brand-summary-fields">
            {[
              ["Business", quick.business_name],
              ["Industry", quick.industry],
              ["Website", quick.website],
              ["Audience", quick.audience],
              ["Tone", quick.tone],
              ["Language", quick.content_language],
              ["Location", quick.location],
              ["Goals", (quick.goals || []).join(", ")],
              ["Personality", (quick.personality || []).join(", ")],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
                <dd className="mt-1 text-sm text-slate-800">{value || "—"}</dd>
              </div>
            ))}
          </dl>
        )}
      </Card>

      <div className="mt-6">
        <Card testId="brand-pillars-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-lg font-semibold text-slate-900">Content pillars</p>
              <p className="mt-1 text-sm text-slate-600">
                The recurring themes PostMint rotates through when it builds your month.
              </p>
            </div>
            <div className="flex gap-2">
              <Btn variant="outline" onClick={suggest} disabled={!!busy} testId="brand-suggest-pillars">
                {busy === "suggest" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Suggest pillars
              </Btn>
              <Btn onClick={save} disabled={!!busy} testId="brand-save-pillars">
                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save pillars
              </Btn>
            </div>
          </div>

          {pillars.length === 0 ? (
            <div className="mt-5">
              <Empty testId="brand-pillars-empty" title="No pillars yet" body="Ask PostMint to suggest a few, or add your own." />
            </div>
          ) : (
            <ul className="mt-5 space-y-3" data-testid="brand-pillars-list">
              {pillars.map((p, i) => (
                <li key={p.id || `${p.name}-${i}`} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      value={p.name}
                      onChange={(e) => update(i, { name: e.target.value })}
                      data-testid={`pillar-name-${i}`}
                      className={`${inputCls} flex-1`}
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={p.enabled !== false}
                        onChange={(e) => update(i, { enabled: e.target.checked })}
                        data-testid={`pillar-enabled-${i}`}
                        className="h-4 w-4 accent-[#F95738]"
                      />
                      Active
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={p.priority || i + 1}
                      onChange={(e) => update(i, { priority: Number(e.target.value) })}
                      data-testid={`pillar-priority-${i}`}
                      className="w-20 rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                      aria-label="Priority"
                    />
                    <Btn
                      variant="danger"
                      onClick={() => setPillars(pillars.filter((_, idx) => idx !== i))}
                      testId={`pillar-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Btn>
                  </div>
                  {p.why && <p className="mt-2 text-xs text-slate-500">{p.why}</p>}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4">
            <Btn
              variant="outline"
              onClick={() => setPillars([...pillars, { name: "", why: "", enabled: true, priority: pillars.length + 1 }])}
              testId="brand-add-pillar"
            >
              <Plus className="h-4 w-4" /> Add pillar
            </Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}
